import { useState } from "react";
import { Sparkles, Info } from "lucide-react";
import SectionCard from "@/components/SectionCard";
import FugaCard from "@/components/FugaCard";
import FunnelChart from "@/components/charts/FunnelChart";
import { FUNNELS_BY_TYPE, DATOS_POR_PERIODO, HOSPITALARIO_POR_PERIODO, COBRANZAS_POR_PERIODO } from "@/mocks/data";
import { JOURNEY_TYPES, formatNum } from "@/lib/constants";
import { useFilters } from "@/context/FiltersContext";
import LeyendaEstado from "@/components/pipeline/LeyendaEstado";

// Períodos que representan ≥3 meses (para mostrar etapa de control)
const PERIODOS_LARGOS = ["Q1 2026", "Q4 2025", "Últimos 6 meses", "Últimos 12 meses"];

export default function Viaje() {
  const [tipo, setTipo] = useState("ambulatorio");
  const current = FUNNELS_BY_TYPE[tipo];
  const { filters } = useFilters();

  // Datos reales del período seleccionado
  const periodoData = DATOS_POR_PERIODO[filters.periodo] || DATOS_POR_PERIODO["Abril 2026"];
  const hospData = HOSPITALARIO_POR_PERIODO[filters.periodo] || HOSPITALARIO_POR_PERIODO["Abril 2026"];
  const cobranzasData = COBRANZAS_POR_PERIODO[filters.periodo] || COBRANZAS_POR_PERIODO["Abril 2026"];

  // Filtrar etapas según período: "Control Post-Atención" solo con ≥3 meses
  const esLargoPlazo = PERIODOS_LARGOS.includes(filters.periodo);
  const funnelBase = esLargoPlazo
    ? current.funnel
    : current.funnel.filter((e) => !e.nota_periodo);

  // Escalar funnel ambulatorio con datos reales del período
  const escalarFunnel = (funnel) => {
    if (tipo === "ambulatorio") {
      // Usar ratio real: consultas DG del período / consultas DG abril
      const baseConsultas = 123718; // DG episodios abril 2026
      const periodoConsultas = periodoData.dg.episodios;
      const ratio = periodoConsultas / baseConsultas;
      return funnel.map((etapa) => ({
        ...etapa,
        pacientes: Math.round(etapa.pacientes * ratio),
      }));
    }
    if (tipo === "hospitalario") {
      const baseIndicaciones = 16431; // indicaciones 6 meses
      const periodoIndicaciones = hospData.indicaciones.total;
      const ratio = periodoIndicaciones / baseIndicaciones;
      return funnel.map((etapa) => ({
        ...etapa,
        pacientes: Math.round(etapa.pacientes * ratio),
      }));
    }
    if (tipo === "cobranzas") {
      const baseEpisodios = 158372;
      const periodoEpisodios = cobranzasData.episodios;
      const ratio = periodoEpisodios / baseEpisodios;
      return funnel.map((etapa) => ({
        ...etapa,
        pacientes: Math.round(etapa.pacientes * ratio),
      }));
    }
    // Urgencia y otros: sin cambio (mock)
    return funnel;
  };

  const funnelFiltrado = escalarFunnel(funnelBase);

  // Escalar fugas y recalcular porcentajes con los volúmenes del funnel escalado
  const escalarFugas = (fugas) => {
    if (!fugas) return [];

    // Construir mapa de volúmenes del funnel escalado para recalcular %
    const volumenByAmbito = {};
    for (const etapa of funnelFiltrado) {
      // Mapear nombre corto de fuga → volumen de la etapa correspondiente
      const key = etapa.ambito.split(" ")[0]; // "Búsqueda", "Identificación", etc.
      volumenByAmbito[key] = etapa.pacientes;
    }
    // Mapeos adicionales para fugas que usan nombres diferentes
    volumenByAmbito["Cancelaciones"] = funnelFiltrado.find((e) => e.ambito.includes("Agendamiento"))?.pacientes || 0;
    volumenByAmbito["Vigentes"] = funnelFiltrado.find((e) => e.ambito.includes("Vigentes"))?.pacientes || 0;
    volumenByAmbito["Indicación"] = funnelFiltrado.find((e) => e.ambito.includes("Indicación"))?.pacientes || 0;

    if (tipo === "ambulatorio" || tipo === "hospitalario") {
      const ratio = tipo === "ambulatorio"
        ? periodoData.dg.episodios / 123718
        : hospData.indicaciones.total / 16431;

      return fugas.map((f) => {
        const perdidosEscalados = f.perdidos != null ? Math.round(f.perdidos * ratio) : null;
        // Recalcular % basado en el volumen de la etapa "desde"
        const baseVolumen = volumenByAmbito[f.desde] || 0;
        const pctRecalculado = baseVolumen > 0 && perdidosEscalados != null
          ? Math.round((perdidosEscalados / baseVolumen) * 1000) / 10
          : f.pct_perdida;

        return {
          ...f,
          perdidos: perdidosEscalados,
          pct_perdida: pctRecalculado,
        };
      });
    }
    return fugas;
  };

  const fugasFiltradas = escalarFugas(current.fugas_criticas);

  // Determinar total de entrada para el período
  const getTotalEntrada = () => {
    if (tipo === "ambulatorio") return funnelFiltrado[0]?.pacientes || 0;
    if (tipo === "hospitalario") return hospData.indicaciones.total;
    if (tipo === "cobranzas") return cobranzasData.episodios;
    return current.total_entrada;
  };

  // Determinar si los datos son reales
  const esReal = tipo === "ambulatorio" || tipo === "hospitalario" || tipo === "cobranzas";

  return (
    <div className="space-y-6" data-testid="viaje-page">
      <div>
        <div className="flex items-center gap-2 text-xs font-medium text-violet-600 mb-2">
          <Sparkles className="w-3.5 h-3.5" /> Funnel detallado por tipo de viaje
        </div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Viaje del Paciente — Funnel Completo</h1>
        <p className="text-sm text-slate-500 mt-1">
          Visualización paso a paso de las {funnelFiltrado.length} etapas del viaje {current.tipo_viaje} con {formatNum(getTotalEntrada())} pacientes de entrada · {filters.periodo}
        </p>
      </div>

      {/* Selector de tipo de viaje */}
      <div className="flex flex-wrap gap-2" data-testid="viaje-type-selector">
        {JOURNEY_TYPES.filter((j) => j.key !== "global").map((j) => (
          <button
            key={j.key}
            data-testid={`viaje-${j.key}`}
            onClick={() => setTipo(j.key)}
            className={`px-4 h-9 rounded-xl text-xs font-semibold transition-all border ${
              tipo === j.key
                ? "text-white border-transparent shadow-md"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
            style={tipo === j.key ? { backgroundColor: j.color } : {}}
          >
            {j.label}
          </button>
        ))}
      </div>

      {/* Badge de datos + período */}
      <div className="flex items-center gap-2 flex-wrap">
        <LeyendaEstado estado={esReal ? "REAL" : "MOCK"} />
        <span className="text-xs text-slate-500">{current.periodo}</span>
        {tipo === "cobranzas" && cobranzasData.nota_periodo && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
            <Info className="w-3 h-3" /> {cobranzasData.mes_referencia}
          </span>
        )}
      </div>

      {/* Nota de control post-atención */}
      {!esLargoPlazo && tipo === "ambulatorio" && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-slate-900 text-sm">Etapa de control no visible</div>
            <div className="text-xs text-slate-600 mt-1">
              La etapa "Control Post-Atención" requiere un período de al menos 3 meses para medir si los pacientes volvieron a control.
              Selecciona "Q1 2026", "Últimos 6 meses" o "Últimos 12 meses" en el filtro de período para verla.
            </div>
          </div>
        </div>
      )}

      {/* KPIs resumen del período — solo ambulatorio y hospitalario */}
      {tipo === "ambulatorio" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Pacientes DG</div>
            <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(periodoData.dg.pacientes)}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Consultas DG</div>
            <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(periodoData.dg.episodios)}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Lab Modulab</div>
            <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(periodoData.modulab.ordenes)}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Bonos IMED</div>
            <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(periodoData.imed.bonos)}</div>
          </div>
        </div>
      )}

      {tipo === "hospitalario" && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Indicaciones Qx</div>
            <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(hospData.indicaciones.total)}</div>
            <div className="text-xs text-slate-500 tabular-nums">{formatNum(hospData.indicaciones.pacientes)} pacientes</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Cirugías SAP</div>
            <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(hospData.cirugias.total)}</div>
            <div className="text-xs text-slate-500 tabular-nums">{formatNum(hospData.cirugias.pacientes)} pacientes</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Conversión</div>
            <div className="mt-1 text-xl font-bold text-emerald-600 tabular-nums">{hospData.conversion_indicacion_cirugia}%</div>
            <div className="text-xs text-slate-500">indicación → cirugía</div>
          </div>
        </div>
      )}

      {tipo === "cobranzas" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Facturación</div>
            <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">${formatNum(cobranzasData.monto_total_mm)}</div>
            <div className="text-xs text-slate-500">MM CLP</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Episodios</div>
            <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(cobranzasData.episodios)}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Vigente</div>
            <div className="mt-1 text-xl font-bold text-emerald-600 tabular-nums">${formatNum(cobranzasData.vigente_0_30_mm)}</div>
            <div className="text-xs text-slate-500">MM CLP ({cobranzasData.pct_vigente}%)</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Mora 31-60d</div>
            <div className="mt-1 text-xl font-bold text-amber-600 tabular-nums">${formatNum(cobranzasData.mora_31_60_mm)}</div>
            <div className="text-xs text-slate-500">MM CLP</div>
          </div>
        </div>
      )}

      {/* Funnel + Fugas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard
          title={`Embudo — ${current.tipo_viaje.charAt(0).toUpperCase() + current.tipo_viaje.slice(1)}`}
          subtitle={`${funnelFiltrado.length} etapas · ${filters.periodo} · ${formatNum(getTotalEntrada())} pacientes`}
          testid="viaje-funnel-chart"
        >
          <FunnelChart data={funnelFiltrado} height={Math.max(420, funnelFiltrado.length * 46)} />
        </SectionCard>

        <SectionCard title="Fugas críticas" subtitle={`Puntos de mayor pérdida · ${filters.periodo}`} testid="viaje-fugas">
          <div className="grid grid-cols-1 gap-3 max-h-[560px] overflow-y-auto pr-1">
            {fugasFiltradas?.length > 0
              ? fugasFiltradas.map((f, i) => <FugaCard key={i} fuga={f} index={i} />)
              : <p className="text-sm text-slate-500 py-4 text-center">Sin fugas críticas registradas para este tipo de viaje</p>
            }
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
